import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Check, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import DemoTryOn from "../components/DemoTryOn";
import { api, formatApiErrorDetail } from "../lib/api";
import { BEFORE, TABS } from "../lib/tryOnData";

export default function TryOnLanding() {
  const [params] = useSearchParams();
  const initial = TABS.find((t) => t.key === params.get("c")) ? params.get("c") : "victorian";
  const [active, setActive] = useState(initial);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const tab = TABS.find((t) => t.key === active);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/waitlist", { email: email.trim(), category: active });
      setJoined(true);
      toast.success("You're on the list — check your inbox.");
    } catch (err) {
      toast.error(err.response ? formatApiErrorDetail(err.response?.data?.detail) : err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onTab = (key) => {
    setActive(key);
    setJoined(false);
    setEmail("");
  };

  return (
    <div className="relative z-10 min-h-screen">
      <Navbar />

      <div className="px-6 md:px-16 py-12 md:py-16 max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="inline-flex items-center gap-2 rounded-full bg-[#F4F1EA] border border-[#1A1A1A]/10 px-4 py-1.5 text-xs uppercase tracking-[0.3em] text-[#6D214F] mb-5">
            <Clock className="w-3.5 h-3.5" /> Launching December 2026
          </span>
          <h1 className="font-serif-display text-4xl md:text-5xl leading-tight text-[#1A1A1A]">The Fitting Room</h1>
          <p className="mt-4 text-base text-[#4A4A4A] leading-relaxed">
            Four vintage worlds, each rendered onto you from a single photo. Choose a collection, see a sample fitting,
            and leave your email to be first through the door.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tryon-tab-${t.key}`}
              onClick={() => onTab(t.key)}
              className={`text-sm px-5 py-2.5 rounded-full border transition-colors duration-300 ${
                active === t.key
                  ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]"
                  : "border-[#1A1A1A]/20 text-[#4A4A4A] hover:border-[#6D214F] hover:text-[#6D214F]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center"
          >
            {/* Demo — before/after photo, or a prompt example for custom */}
            <div className="max-w-sm mx-auto w-full">
              {active === "custom" ? (
                <div data-testid="prompt-example" className="rounded-2xl border border-[#1A1A1A]/10 bg-[#F4F1EA] p-6 shadow-[0_20px_50px_-24px_rgba(26,20,15,0.4)]">
                  <p className="text-xs uppercase tracking-[0.3em] text-[#8B5A2B] mb-4">Example prompt</p>
                  <div className="rounded-xl bg-[#FDFBF7] border border-[#1A1A1A]/10 p-5">
                    <p className="text-base text-[#1A1A1A] leading-relaxed font-serif-display italic">
                      "A floor-length Edwardian gown in dusty-rose silk — high lace collar, puffed
                      sleeves and tiny lace covered buttons on the cuffs of the sleeves."
                    </p>
                  </div>
                  <div className="mt-5 flex items-center justify-end">
                    <span className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] text-xs uppercase tracking-[0.2em] px-5 py-2.5 rounded-full">
                      <Sparkles className="w-3.5 h-3.5" /> Render my design
                    </span>
                  </div>
                </div>
              ) : (
                <DemoTryOn before={tab.before || BEFORE} after={tab.after} aspect={tab.aspect} shareKey={tab.key} shareLabel={tab.label} />
              )}
            </div>

            {/* Coming soon + signup */}
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-[#8B5A2B] mb-4">{tab.label}</p>
              <h2 className="font-serif-display text-3xl md:text-4xl leading-tight text-[#1A1A1A]">Coming this December.</h2>
              <div className="w-16 h-px bg-[#1A1A1A]/20 my-6" />
              <p className="text-base text-[#4A4A4A] leading-relaxed max-w-md">{tab.copy}</p>

              <div className="mt-8">
                {joined ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    data-testid="waitlist-success"
                    className="flex items-start gap-3 rounded-2xl border border-[#6D214F]/30 bg-[#F4F1EA] p-5"
                  >
                    <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-[#6D214F] text-[#FDFBF7] flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </span>
                    <div>
                      <p className="font-serif-display text-lg text-[#1A1A1A]">You're on the list.</p>
                      <p className="text-sm text-[#4A4A4A] mt-1">
                        We've sent a confirmation to your inbox. You'll hear from us the moment the {tab.label} fitting room opens.
                      </p>
                    </div>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} data-testid="waitlist-form">
                    <label className="block text-xs uppercase tracking-[0.3em] text-[#8B5A2B] mb-3">
                      Be notified when it launches
                    </label>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A4A4A]/60" />
                        <input
                          data-testid="waitlist-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="your@email.com"
                          className="w-full bg-[#F4F1EA] border border-[#1A1A1A]/10 rounded-full pl-11 pr-5 py-3.5 text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/60 focus:outline-none focus:border-[#6D214F] transition-colors"
                        />
                      </div>
                      <button
                        data-testid="waitlist-submit"
                        type="submit"
                        disabled={submitting}
                        className="bg-[#1A1A1A] text-[#FDFBF7] px-7 py-3.5 rounded-full hover:bg-[#6D214F] transition-colors duration-300 disabled:opacity-50 whitespace-nowrap"
                      >
                        {submitting ? "Adding…" : "Notify me"}
                      </button>
                    </div>
                    <p className="text-xs text-[#4A4A4A] mt-3">One email at launch. No spam, ever.</p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
